import { useEffect, useState, type FormEvent } from 'react';

import { AccountDeletionError, requestPatientAccountDeletion } from '../api/accountDeletion';
import { Button, Input } from '../components/ui';
import styles from './DeleteAccountPage.module.scss';

const supportEmail = 'whiteandflex@gmail.com';
const emailLink = `mailto:${supportEmail}?subject=${encodeURIComponent('White&Flex — запит на видалення акаунту / account deletion')}&body=${encodeURIComponent('Прошу видалити мій акаунт White&Flex та пов’язані персональні дані.\nEmail акаунту / Account email: \n\nPlease delete my White&Flex account and associated personal data.')}`;

export function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Видалення акаунту | White&Flex';
    return () => { document.title = previous; };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || !confirmed) return;
    setError('');
    setPending(true);
    try {
      setScheduledFor(await requestPatientAccountDeletion(email, password));
      setEmail('');
    } catch (cause) {
      setError(cause instanceof AccountDeletionError && cause.reason === 'credentials'
        ? 'Не вдалося підтвердити вхід. Перевірте email і пароль або надішліть запит на email нижче.'
        : cause instanceof AccountDeletionError && cause.reason === 'patient'
          ? 'Ця форма призначена для акаунтів пацієнтів. Для інших акаунтів зверніться на email нижче.'
          : 'Запит не підтверджено. Спробуйте ще раз або надішліть запит на email нижче.');
    } finally {
      setPassword('');
      setPending(false);
    }
  }

  return (
    <main className={styles.page} lang="uk">
      <article className={styles.content}>
        <header>
          <a className={styles.brand} href="https://whiteandflex.com">White&amp;Flex</a>
          <p className={styles.eyebrow}>Акаунт і персональні дані</p>
          <h1>Видалення акаунту</h1>
          <p className={styles.intro}>Подайте запит на видалення акаунту пацієнта White&amp;Flex та пов’язаних персональних даних. Встановлювати застосунок не потрібно.</p>
        </header>

        <section aria-labelledby="deletion-request" className={styles.section}>
          <h2 id="deletion-request">Запит через email і пароль</h2>
          {scheduledFor ? (
            <div role="status" className={styles.notice}>
              <h3>Запит прийнято</h3>
              <p>Видалення заплановано на <strong>{new Intl.DateTimeFormat('uk-UA', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(scheduledFor))}</strong>.</p>
              <p>До цієї дати запит можна скасувати в налаштуваннях застосунку White&amp;Flex або через службу підтримки.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <Input id="deletion-email" label="Email акаунту" type="email" autoComplete="username" required value={email} onChange={event => setEmail(event.target.value)} disabled={pending} />
              <Input id="deletion-password" label="Пароль" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} disabled={pending} />
              <label className={styles.confirmation}>
                <input type="checkbox" required checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={pending} />
                <span>Підтверджую запит на видалення мого акаунту та пов’язаних персональних даних. Після завершення видалення відновити їх буде неможливо.</span>
              </label>
              {error && <p role="alert" className={styles.error}>{error}</p>}
              <Button variant="dangerSolid" type="submit" disabled={pending}>{pending ? 'Надсилання…' : 'Надіслати запит на видалення'}</Button>
            </form>
          )}
        </section>

        <section aria-labelledby="email-request" className={styles.section}>
          <h2 id="email-request">Вхід через Google / Apple або немає доступу?</h2>
          <p>Надішліть лист на <a href={emailLink}>{supportEmail}</a> з темою «Видалення акаунту White&amp;Flex» та email вашого акаунту. Підтримка підтвердить вашу особу та прийняття запиту. Не надсилайте пароль, медичні фото чи копії документів у першому листі.</p>
          <p>Посилання відкриває поштову програму. Запит буде надіслано лише після того, як ви відправите лист.</p>
        </section>

        <section aria-labelledby="retention" className={styles.section}>
          <h2 id="retention">Що відбувається з даними</h2>
          <p>Строк зберігання після прийняття запиту — 30 днів. У цей період можна скасувати видалення через налаштування застосунку або підтримку.</p>
          <p>Запит охоплює облікові дані, профіль пацієнта, історію лікування, повідомлення, фото, відео та інші пов’язані з акаунтом файли.</p>
          <p>Видалення застосунку з телефона саме по собі не видаляє акаунт.</p>
        </section>

        <section className={styles.section} lang="en" aria-labelledby="english-summary">
          <h2 id="english-summary">Request account deletion</h2>
          <p>This page is for White&amp;Flex patient accounts. Use the form with your account email and password, or email <a href={emailLink}>{supportEmail}</a> from your account address. Google/Apple users can use the email option. Support will verify ownership and confirm acceptance. Never email your password.</p>
          <p>Retention after an accepted deletion request: 30 days. You can cancel during this period in the app settings or through support. The request covers your account, patient profile, treatment history, messages and associated files. Uninstalling the app does not delete your account.</p>
        </section>
      </article>
    </main>
  );
}
